import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import OrderDetailPanel from './OrderDetailPanel';
import type { Order } from './orderDetailTypes';
import { ALL_STATUSES, ORDER_STATUS_KEYS, statusColors } from './orderDetailTypes';

export default function OrdersTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, _setLimit] = useState(20);

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [currentPage, limit, activeSearch, statusFilter]);

  const loadOrders = async (): Promise<Order[]> => {
    try {
      setLoading(true);
      const params: any = { 
        page: currentPage,
        limit: limit,
      };
      
      if (activeSearch) params.search = activeSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/orders', { params });
      const list = response.data.data || [];
      setOrders(list);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      return list;
    } catch (error) {
      console.error('Error loading orders:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    const key = ORDER_STATUS_KEYS[status];
    return key ? t(`orders.${key}`) : status;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('orders.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative flex items-center">
            <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('orders.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
            />
            <button
              onClick={handleSearch}
              className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
            >
              {t('common.search')}
            </button>
          </div>
          <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="all">{t('orders.allStatus')}</option>
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{getStatusLabel(s)}</option>
              ))}
            </select>
            {(search || statusFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.clearFilters')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.orderId')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.customer')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.recipe')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.quantity')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.totalPrice')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.status')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.paymentMethod')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.date')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('orders.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    #{order.orderNumber ?? order._id.substring(0, 8)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order.userId?.name || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.userId?.email || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {(order.items?.[0]?.recipeImage ?? order.recipeImage) && (
                        <img
                          src={order.items?.[0]?.recipeImage ?? order.recipeImage}
                          alt={order.items?.[0]?.recipeTitle ?? order.recipeTitle ?? 'Order'}
                          className="h-10 w-10 rounded object-cover mr-3"
                        />
                      )}
                      <div className="text-sm font-medium text-gray-900">
                        {order.items?.length
                          ? order.items.length === 1
                            ? order.items[0].recipeTitle
                            : `${order.items[0].recipeTitle} (+${order.items.length - 1} more)`
                          : (order.recipeTitle ?? '—')}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.items?.length
                      ? order.items.reduce((sum, i) => sum + i.quantity, 0)
                      : (order.quantity ?? '—')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {order.totalPriceEGP.toFixed(2)} EGP
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700">
                      {order.paymentMethod === 'cod' && t('orders.cashOnDelivery')}
                      {order.paymentMethod === 'instapay' && 'InstaPay'}
                      {order.paymentMethod === 'wallet' && t('manualPayments.mobileWallet')}
                      {!order.paymentMethod && t('orders.cashOnDelivery')}
                    </div>
                    {order.paymentStatus === 'approved' && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Payment: Received
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(order.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex flex-wrap items-center gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowDetailsModal(true);
                        }}
                        className="text-primary-orange hover:text-orange-600"
                      >
                        {t('common.view')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && (
          <div className="text-center py-8 text-gray-500">{t('orders.noOrders')}</div>
        )}
        {totalPages > 1 && (
          <div className={`bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className={`flex-1 flex justify-between sm:hidden ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 ${isRtl ? 'mr-3 ml-0' : 'ml-3'}`}
              >
                {t('common.next')}
              </button>
            </div>
            <div className={`hidden sm:flex-1 sm:flex sm:items-center sm:justify-between ${isRtl ? 'sm:flex-row-reverse' : ''}`}>
              <div>
                <p className="text-sm text-gray-700">
                  {t('common.showing', { from: (currentPage - 1) * limit + 1, to: Math.min(currentPage * limit, total), total })}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-primary-orange border-primary-orange text-white'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                      return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                    }
                    return null;
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className={`relative mx-auto w-11/12 max-w-2xl border shadow-lg rounded-md bg-white min-h-0 max-h-[calc(100vh-4rem)] flex flex-col ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
            <div className={`flex justify-between items-center p-5 pb-0 flex-shrink-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-bold text-gray-900">{t('orders.details')}</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedOrder(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 pt-4 overflow-y-auto flex-1 min-h-0">
              <OrderDetailPanel
                order={selectedOrder}
                replaceOrder={setSelectedOrder}
                reloadOrder={async () => {
                  const oid = selectedOrder._id;
                  const list = await loadOrders();
                  const u = list.find((o) => o._id === oid);
                  if (u) {
                    setSelectedOrder(u);
                    return u;
                  }
                  return null;
                }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
